import { forceLogout } from '@/contexts/auth/force-logout'
import { getAccessToken } from '@/contexts/auth/token-storage'

import { classifyAuthFailure, isAuthRoute } from './auth-failure'
import { getDefaultHeaders } from './get-default-headers'
import { ensureFreshToken } from './refresh-session'
import type { ApiResponse } from './types/ApiResponse'
import { HttpError } from './types/errors/HttpError'
import { JsonError } from './types/errors/JsonError'
import { NetworkError } from './types/errors/NetworkError'

type QueryParamValue = string | number | boolean | null | undefined

interface RequestOptions {
  headers?: Record<string, string>
  queryParams?: Record<string, QueryParamValue>
  body?: unknown
}

const request =
  (method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE') =>
  async <T>(
    baseUrl: string,
    path: string,
    reqOptions?: RequestOptions,
  ): Promise<ApiResponse<T>> => {
    const url = new URL(path, baseUrl)
    if (reqOptions?.queryParams) {
      for (const [key, value] of Object.entries(reqOptions.queryParams)) {
        if (value !== undefined && value !== null) {
          url.searchParams.set(key, value.toString())
        }
      }
    }
    const bodyString = reqOptions?.body
      ? JSON.stringify(reqOptions.body)
      : undefined

    // Resolved per attempt so a replay picks up the renewed token.
    const send = async (): Promise<Response> => {
      try {
        return await fetch(url, {
          method,
          headers: {
            ...getDefaultHeaders(),
            ...reqOptions?.headers,
          },
          body: bodyString,
        })
      } catch (error) {
        throw new NetworkError(`Failed to fetch ${method} ${url}`, {
          cause: error,
        })
      }
    }

    const parse = async (res: Response): Promise<unknown> => {
      if (res.status === 204 || res.status === 205 || res.status === 304) {
        return null
      }

      const raw = await res.text().catch(() => '')
      if (!raw) return null

      try {
        return JSON.parse(raw)
      } catch (error) {
        throw new JsonError(
          `Failed to parse response body for ${method} ${url}`,
          { cause: error },
        )
      }
    }

    const tokenUsed = getAccessToken()

    // 1. http request
    let res = await send()

    // 2. parse response body
    let resBody = await parse(res)

    // 3. renew the session when the backend says the credentials are stale
    if (!res.ok && !isAuthRoute(url.pathname)) {
      const failure = classifyAuthFailure(res.status, resBody)

      if (failure === 'FATAL') {
        forceLogout()
      } else if (failure === 'RENEWABLE') {
        const freshToken = await ensureFreshToken(tokenUsed)

        if (freshToken) {
          res = await send()
          resBody = await parse(res)

          if (!res.ok && classifyAuthFailure(res.status, resBody) === 'FATAL') {
            forceLogout()
          }
        }
      }
    }

    // 4. handle error response
    if (!res.ok) {
      throw new HttpError(`${method} ${url} [${res.status}]`, {
        response: {
          body: resBody,
          statusCode: res.status,
          statusText: res.statusText,
          headers: res.headers,
        },
      })
    }

    // 5. handle success response
    return {
      body: resBody as T,
      statusCode: res.status,
      statusText: res.statusText,
      headers: res.headers,
    }
  }

export { request }
